import { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { ASSETS } from "@/lib/assets";

const BAR_DELAYS = [0, 0.2, 0.4, 0.15, 0.35, 0.1, 0.45, 0.25, 0.05, 0.3, 0.12, 0.42];

export default function RadioPlayer() {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);
  const [streamUrl, setStreamUrl] = useState(null);
  const [nowPlaying, setNowPlaying] = useState({
    artist: "KSSR",
    title: "Loading broadcast…",
    recent: [],
  });

  useEffect(() => {
    let mounted = true;
    api.get("/radio/config").then((r) => mounted && setStreamUrl(r.data.stream_url)).catch(() => {});
    const fetchNP = () =>
      api
        .get("/radio/now-playing")
        .then((r) => mounted && setNowPlaying(r.data))
        .catch(() => {});
    fetchNP();
    const id = setInterval(fetchNP, 20000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);

  const togglePlay = async () => {
    const el = audioRef.current;
    if (!el || !streamUrl) return;
    if (playing) {
      el.pause();
      setPlaying(false);
    } else {
      try {
        setLoading(true);
        el.src = streamUrl; // reset src to grab live edge
        el.load();
        await el.play();
        setPlaying(true);
      } catch (e) {
        setPlaying(false);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleVolume = (v) => {
    setVolume(v);
    setMuted(v === 0);
    if (audioRef.current) audioRef.current.volume = v;
  };

  return (
    <div
      data-testid="radio-player"
      className="relative bg-surface crimson-glow p-8 md:p-10"
    >
      <audio ref={audioRef} preload="none" />

      {/* Station logo */}
      <div className="flex justify-center mb-6">
        <img src={ASSETS.kssrLogo} alt="KSSR — Sorrow & Serenity Radio" className="w-40 md:w-48 object-contain" />
      </div>

      {/* Status + now playing */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <span
          data-testid="radio-live-badge"
          className="inline-flex items-center gap-2 px-3 py-1.5 bg-crimson/20 border border-crimson/50 font-label uppercase tracking-[0.2em] text-[11px] text-crimson-bright"
        >
          <span className={`h-2 w-2 rounded-full bg-crimson-bright ${playing ? "animate-pulse-glow" : ""}`} />
          Live Broadcast
        </span>
        <span className="font-label uppercase tracking-[0.25em] text-[11px] text-slate">
          KSSR · 128kbps
        </span>
      </div>

      <div className="mt-8">
        <p className="font-label uppercase tracking-[0.3em] text-[11px] text-slate mb-3">Now Playing</p>
        <h3
          data-testid="now-playing-title"
          className="font-display text-2xl md:text-3xl text-softwhite leading-tight"
        >
          {nowPlaying.title}
        </h3>
        <p data-testid="now-playing-artist" className="mt-2 text-crimson-bright font-label uppercase tracking-[0.15em] text-sm">
          {nowPlaying.artist}
        </p>
      </div>

      {/* Visualizer */}
      <div className={`mt-8 flex items-end justify-center gap-1.5 h-20 ${playing ? "" : "eq-paused"}`} aria-hidden>
        {BAR_DELAYS.map((d, i) => (
          <div
            key={i}
            className="eq-bar"
            style={{ height: "100%", animationDelay: `${d}s`, animationDuration: `${0.7 + (i % 4) * 0.15}s` }}
          />
        ))}
      </div>

      {/* Controls */}
      <div className="mt-8 flex items-center gap-6">
        <button
          onClick={togglePlay}
          data-testid="radio-play-toggle"
          className="h-16 w-16 flex items-center justify-center bg-crimson hover:bg-crimson-bright text-softwhite transition-colors duration-300 shrink-0"
          aria-label={playing ? "Pause" : "Play"}
        >
          {loading ? (
            <Loader2 size={26} className="animate-spin" />
          ) : playing ? (
            <Pause size={26} />
          ) : (
            <Play size={26} className="ml-1" />
          )}
        </button>

        <div className="flex items-center gap-3 flex-1 max-w-xs">
          <button
            onClick={() => handleVolume(muted ? 0.8 : 0)}
            data-testid="radio-mute-toggle"
            className="text-slate hover:text-softwhite transition-colors"
            aria-label="Mute"
          >
            {muted || volume === 0 ? <VolumeX size={20} /> : <Volume2 size={20} />}
          </button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={muted ? 0 : volume}
            onChange={(e) => handleVolume(parseFloat(e.target.value))}
            data-testid="radio-volume-slider"
            className="w-full accent-crimson cursor-pointer"
            aria-label="Volume"
          />
        </div>
      </div>

      {/* Recently played */}
      {nowPlaying.recent && nowPlaying.recent.length > 0 && (
        <div className="mt-8 pt-6 border-t border-slate/15">
          <p className="font-label uppercase tracking-[0.3em] text-[10px] text-slate mb-4">Recently Played</p>
          <ul className="space-y-2">
            {nowPlaying.recent.map((t, i) => (
              <li key={i} className="flex items-baseline gap-3 text-sm">
                <span className="text-crimson-bright/70 font-label text-xs">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-softwhite/90">{t.title}</span>
                <span className="text-slate">— {t.artist}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
